import z from 'zod';

export type NumericStringOptions = {
  min?: number;
  max?: number;
  isWhole?: boolean;
};

// `emptyAsUndefined` - `true` means an empty string is treated as undefined
//                      `false` means an empty is invalid - show an error
export type OptionalNumericStringOptions = NumericStringOptions & { emptyAsUndefined?: boolean };

function validateNumber(
  val: string,
  options: NumericStringOptions | undefined,
  ctx: z.core.$RefinementCtx<string>,
) {
  const num = Number(val);
  if (Number.isNaN(num)) {
    ctx.addIssue({ code: 'invalid_type', expected: 'number' });
    return z.NEVER;
  }

  if (options?.min !== undefined && num < options.min) {
    ctx.addIssue({ code: 'too_small', minimum: options.min, origin: 'number' });
    return z.NEVER;
  }

  if (options?.max !== undefined && num > options.max) {
    ctx.addIssue({ code: 'too_big', maximum: options.max, origin: 'number' });
    return z.NEVER;
  }

  if (options?.isWhole) {
    const isWholeNumber = Number.isInteger(num);
    if (!isWholeNumber) {
      ctx.addIssue({ code: 'invalid_type', expected: 'integer' });
      return z.NEVER;
    }
  }

  return num;
}

// Validates a string that contains a number within a given range
export const numericString = (options?: NumericStringOptions) => {
  return z
    .string()
    .nonempty()
    .transform((val, ctx) => {
      if (val.trim() === '') {
        ctx.addIssue({ code: 'invalid_type', expected: 'number' });
        return z.NEVER;
      }

      return validateNumber(val, options, ctx);
    });
};

// Validates a string that contains a number within a given range
export const optionalNumericString = (options?: OptionalNumericStringOptions) => {
  return z
    .string()
    .transform((val, ctx) => {
      if (val.trim() === '') {
        if (options?.emptyAsUndefined) {
          return undefined;
        }

        ctx.addIssue({ code: 'invalid_type', expected: 'number' });
        return z.NEVER;
      }

      return validateNumber(val, options, ctx);
    })
    .optional();
};
